import React, { useMemo, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { calculateSmartTicks } from '../../utils/scaleHelper';
import { isWithin1DBounds } from '../../utils/boundsHelper';
import { getTimeframeSeparation, getPeriodLabel, getSeparatorIndices } from '../../utils/timeUtils';

// Helper to convert hex to rgba
const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface FloorGridTextureProps {
  data: any[];
  timeMin: number;
  extendedTimeMax: number;
  startX: number;
  width: number;
  chartWidth?: number;
  zLimit: number;
  zScale: number;
  floorY: number;
  showGrid?: boolean;
  gridColor?: string;
  gridOpacity?: number;
  gridThickness?: number;
  targetTicksX?: number;
  targetTicksZ?: number;
  /** false: as linhas de força são desenhadas ao vivo fora da textura (LiveScaleTicks). */
  drawForceLines?: boolean;
  timeframe?: string;
  showPeriodSeparators?: boolean;
  periodColor?: string;
  periodOpacity?: number;
  periodLineStyle?: string;
  periodLineWidth?: number;
  showPeriodLabels?: boolean;
  colorUp?: string;
  colorDown?: string;
}

export const FloorGridTexture: React.FC<FloorGridTextureProps> = ({
  data,
  timeMin,
  extendedTimeMax,
  startX,
  width,
  chartWidth,
  zLimit,
  zScale,
  floorY,
  showGrid = true,
  gridColor = '#333333',
  gridOpacity = 0.4,
  gridThickness = 1,
  targetTicksX = 8,
  targetTicksZ = 8,
  drawForceLines = true,
  timeframe = '1D',
  showPeriodSeparators = false,
  periodColor = '#ffffff',
  periodOpacity = 0.3,
  periodLineStyle = 'dashed',
  periodLineWidth = 1,
  showPeriodLabels = true,
  colorUp = '#00ff00',
  colorDown = '#ff0000',
}) => {
  const { gl } = useThree();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textureRef = useRef<THREE.CanvasTexture | null>(null);
  const meshRef = useRef<THREE.Mesh>(null);

  // 1. Time Ticks for vertical grid lines (X axis)
  // Stabilize time range to avoid recalculation on high-frequency price ticks
  const datasetTimeRange = useMemo(() => {
    if (!data || data.length < 2) return null;
    return {
      tMin: data[0].timestamp || 0,
      tMax: extendedTimeMax || data[data.length - 1].timestamp || 1,
      lastCandleTs: data[data.length - 1].timestamp || 1,
      length: data.length,
      dataRef: data
    };
  }, [data?.length, extendedTimeMax, timeframe]);

  const timeTicks = useMemo(() => {
    if (!showGrid || !datasetTimeRange) return [];
    const { tMin, tMax, lastCandleTs, dataRef } = datasetTimeRange;
    if (tMin >= tMax) return [];
    
    const cw = chartWidth ?? width;
    const smartTime = calculateSmartTicks(tMin, tMax, 'time', targetTicksX);
    
    return smartTime.primary.map(t => {
      if (t.value > lastCandleTs) {
        const timePerPixel = (lastCandleTs - tMin) / cw;
        const extraTime = t.value - lastCandleTs;
        const extraPixels = extraTime / timePerPixel;
        return startX + cw + extraPixels;
      }
      let closestIdx = 0;
      let minDiff = Infinity;
      for (let i = 0; i < dataRef.length; i++) {
        const diff = Math.abs((dataRef[i].timestamp || 0) - t.value);
        if (diff < minDiff) {
          minDiff = diff;
          closestIdx = i;
        }
      }
      return startX + dataRef[closestIdx].time * cw;
    }).filter(x => isWithin1DBounds(x, startX, startX + width));
  }, [datasetTimeRange, startX, width, chartWidth, showGrid, targetTicksX]);

  // 2. Force Ticks for horizontal grid lines (Z axis)
  const forceTicks = useMemo(() => {
    if (!showGrid || !drawForceLines) return { primary: [], secondary: [] };
    const maxForceVal = zLimit / zScale;
    const smartTicks = calculateSmartTicks(-maxForceVal, maxForceVal, 'force', targetTicksZ);
    return {
      primary: smartTicks.primary.filter(t => t.value >= -maxForceVal && t.value <= maxForceVal),
      secondary: smartTicks.secondary.filter(t => t.value >= -maxForceVal && t.value <= maxForceVal)
    };
  }, [showGrid, zLimit, zScale, targetTicksZ, drawForceLines]);

  // 3. Period Separators
  const separators = useMemo(() => {
    if (!showPeriodSeparators || !data || data.length < 2) return [];
    const seps: { x: number; label: string }[] = [];
    
    const sepIndices = getSeparatorIndices(data, timeframe);
    const cw = chartWidth ?? width;

    for (const sep of sepIndices) {
      const xPos = startX + data[sep.index].time * cw;
      if (isWithin1DBounds(xPos, startX, startX + width)) {
        seps.push({
          x: xPos,
          label: sep.label,
        });
      }
    }
    return seps;
  }, [data?.length, timeframe, startX, width, chartWidth, showPeriodSeparators]);

  // Initialize canvas and texture once
  useEffect(() => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
      canvasRef.current.width = 1024;
      canvasRef.current.height = 1024;
    }
    if (!textureRef.current) {
      const tex = new THREE.CanvasTexture(canvasRef.current);
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      textureRef.current = tex;
    }

    return () => {
      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }
      canvasRef.current = null;
    };
  }, []);

  // 4. Update Texture contents
  useEffect(() => {
    let animationFrameId: number;
    let timeoutId: NodeJS.Timeout;
    const draw = () => {
      if (!canvasRef.current || !textureRef.current || width <= 0 || zLimit <= 0) {
        return;
      }

    const canvas = canvasRef.current;
    const canvasWidth = 1024;
    const canvasHeight = 1024;

    if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw Background Zones for Force
    if (colorUp && colorDown) {
      // Top half: Sellers (Negative force) -> colorDown
      ctx.fillStyle = hexToRgba(colorDown, 0.04);
      ctx.fillRect(0, 0, canvasWidth, canvasHeight / 2);
      
      // Bottom half: Buyers (Positive force) -> colorUp
      ctx.fillStyle = hexToRgba(colorUp, 0.04);
      ctx.fillRect(0, canvasHeight / 2, canvasWidth, canvasHeight / 2);
    }

    const toCanvasX = (x3D: number) => ((x3D - startX) / width) * canvasWidth;
    const toCanvasY = (z3D: number) => {
      const normZ = (z3D + zLimit) / (2 * zLimit);
      return normZ * canvasHeight;
    };

    if (showGrid) {
      // Draw Time Lines (X-axis)
      if (timeTicks.length > 0) {
        ctx.save();
        ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.85);
        ctx.lineWidth = Math.max(1, gridThickness);
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        timeTicks.forEach(tx => {
          const px = Math.floor(toCanvasX(tx)) + 0.5;
          if (px >= 0 && px <= canvasWidth) {
            ctx.moveTo(px, 0);
            ctx.lineTo(px, canvasHeight);
          }
        });
        ctx.stroke();
        ctx.restore();
      }

      // Secondary Force Lines
      if (forceTicks.secondary.length > 0) {
        ctx.save();
        ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.45);
        ctx.lineWidth = Math.max(1, gridThickness * 0.8);
        ctx.setLineDash([2, 4]);
        ctx.beginPath();
        forceTicks.secondary.forEach(t => {
          if (t.value === 0) return;
          const py = Math.floor(toCanvasY(t.value * zScale)) + 0.5;
          if (py >= 0 && py <= canvasHeight) {
            ctx.moveTo(0, py);
            ctx.lineTo(canvasWidth, py);
          }
        });
        ctx.stroke();
        ctx.restore();
      }

      // Primary Force Lines
      if (forceTicks.primary.length > 0) {
        ctx.save();
        forceTicks.primary.forEach((t) => {
          const py = Math.floor(toCanvasY(t.value * zScale)) + 0.5;
          if (py >= 0 && py <= canvasHeight) {
            ctx.beginPath();
            if (t.value === 0) {
               ctx.strokeStyle = gridColor;
               ctx.globalAlpha = Math.min(1, gridOpacity * 1.5);
               ctx.lineWidth = Math.max(2, gridThickness * 1.5);
               ctx.setLineDash([4, 4]);
            } else {
               ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.85);
               ctx.lineWidth = Math.max(1, gridThickness);
               ctx.setLineDash([]);
               ctx.globalAlpha = 1.0;
            }
            
            ctx.moveTo(0, py);
            ctx.lineTo(canvasWidth, py);
            ctx.stroke();
          }
        });
        ctx.restore();
      }
    }

    // Draw Period Separators
    if (showPeriodSeparators && separators.length > 0) {
      ctx.save();
      ctx.strokeStyle = hexToRgba(periodColor, periodOpacity * 0.9);
      ctx.lineWidth = Math.max(1, periodLineWidth * 1.5);
      
      if (periodLineStyle === 'dashed') {
        ctx.setLineDash([6, 6]);
      } else if (periodLineStyle === 'dotted') {
        ctx.setLineDash([2, 4]);
      } else {
        ctx.setLineDash([]);
      }
      
      ctx.beginPath();
      separators.forEach((sep) => {
        const px = Math.floor(toCanvasX(sep.x)) + 0.5;
        if (px >= 0 && px <= canvasWidth) {
          ctx.moveTo(px, 0);
          ctx.lineTo(px, canvasHeight);
        }
      });
      ctx.stroke();

      if (showPeriodLabels) {
        ctx.font = '600 14px "Inter", ui-sans-serif, system-ui, sans-serif';
        ctx.textBaseline = 'top';
        
        let lastDrawnLabelPx = -999;
        const MIN_LABEL_DISTANCE_PX = 80;

        separators.forEach((sep) => {
          const px = toCanvasX(sep.x);
          if (px < 0 || px > canvasWidth) return;
          if (px - lastDrawnLabelPx < MIN_LABEL_DISTANCE_PX) return; // Prevent overlapping label collision
          
          lastDrawnLabelPx = px;
          ctx.fillStyle = hexToRgba(periodColor, Math.min(1.0, periodOpacity * 2.2));
          const yPos = 12;
          
          if (px < 50) {
            ctx.textAlign = 'left';
            ctx.fillText(sep.label, Math.max(10, px + 4), yPos);
          } else if (px > canvasWidth - 70) {
            ctx.textAlign = 'right';
            ctx.fillText(sep.label, Math.min(canvasWidth - 10, px - 4), yPos);
          } else {
            ctx.textAlign = 'center';
            ctx.fillText(sep.label, px, yPos);
          }
        });
      }
      ctx.restore();
    }

    textureRef.current.needsUpdate = true;
    };
    timeoutId = setTimeout(() => { animationFrameId = requestAnimationFrame(draw); }, 100);
    return () => { clearTimeout(timeoutId); if (animationFrameId) cancelAnimationFrame(animationFrameId); };
  }, [
    timeTicks, forceTicks, separators,
    showGrid, startX, width, zLimit, zScale, 
    gridColor, gridOpacity, gridThickness,
    showPeriodSeparators, periodColor, periodOpacity, periodLineStyle, periodLineWidth, showPeriodLabels,
    colorUp, colorDown
  ]);

  if (!textureRef.current) return null;

  return (
    <mesh ref={meshRef} position={[startX + width / 2, floorY, 0]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={-5}>
      <planeGeometry args={[width, 2 * zLimit]} />
      <meshBasicMaterial 
        map={textureRef.current} 
        transparent 
        opacity={1.0}
        depthWrite={false}
        depthTest={true}
      />
    </mesh>
  );
};
